// Shared, pure, dependency-free formatters for the Usage tab (and any
// future numeric display). These are the single source of truth for byte +
// count formatting. The DataTab / VersionsTab `fmtSize` copies (which
// mislabeled base-1024 math as "KB"/"MB") were removed in favour of
// formatBytes (F4) — don't reintroduce a per-page byte formatter.

const BYTE_UNITS = ['B', 'KiB', 'MiB', 'GiB', 'TiB'] as const

// formatBytes renders a byte count with base-1024 (binary) units and the
// correct IEC labels. Raw bytes (< 1 KiB) print as a whole number; anything
// at or above 1 KiB prints with `maxFractionDigits` decimals (default 1),
// trailing zeros trimmed. Reads well at both MiB (100 MiB storage cap) and
// GiB (multi-GiB bandwidth) scale. Negative inputs are clamped to 0.
export function formatBytes(
  bytes: number,
  opts?: { maxFractionDigits?: number },
): string {
  const maxFractionDigits = opts?.maxFractionDigits ?? 1
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B'

  let value = bytes
  let unit = 0
  while (value >= 1024 && unit < BYTE_UNITS.length - 1) {
    value /= 1024
    unit++
  }

  if (unit === 0) {
    // Raw bytes: always a whole number.
    return `${Math.round(value)} B`
  }
  // Trim trailing zeros (e.g. "1.0 MiB" -> "1 MiB", "1.50 GiB" -> "1.5 GiB").
  const formatted = value.toFixed(maxFractionDigits).replace(/\.?0+$/, '')
  return `${formatted} ${BYTE_UNITS[unit]}`
}

// formatCount renders a compact request/event count: < 1000 as-is, then K/M/B
// suffixes (base-1000) with up to two trimmed decimals (e.g. 50000 -> "50K",
// 1_240_000 -> "1.24M"). Negative/non-finite inputs render as "0".
export function formatCount(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '0'
  if (n < 1000) return `${Math.round(n)}`

  const units: Array<{ value: number; suffix: string }> = [
    { value: 1_000_000_000, suffix: 'B' },
    { value: 1_000_000, suffix: 'M' },
    { value: 1_000, suffix: 'K' },
  ]
  for (const { value, suffix } of units) {
    if (n >= value) {
      const scaled = n / value
      const formatted = scaled.toFixed(2).replace(/\.?0+$/, '')
      return `${formatted}${suffix}`
    }
  }
  return `${Math.round(n)}`
}

// formatCurrency renders a Stripe minor-unit amount (e.g. cents) in the given
// ISO currency, using the browser's Intl number formatting (e.g. 180/"usd" ->
// "$1.80"). Zero-decimal currencies (JPY) are handled by Intl automatically; we
// pass the major-unit value, so we divide by the currency's minor-unit scale.
// Falls back to a plain "<code> <amount>" string if Intl rejects the currency.
export function formatCurrency(amountMinor: number, currency: string): string {
  const code = (currency || 'usd').toUpperCase()
  try {
    const fmt = new Intl.NumberFormat(undefined, { style: 'currency', currency: code })
    // resolvedOptions().maximumFractionDigits gives the currency's decimal
    // places (2 for USD, 0 for JPY); 10^that is the minor-unit scale.
    const digits = fmt.resolvedOptions().maximumFractionDigits ?? 2
    return fmt.format(amountMinor / 10 ** digits)
  } catch {
    return `${code} ${(amountMinor / 100).toFixed(2)}`
  }
}

// percent returns used/cap as a 0–100 percentage, clamped to that range.
// Returns null when `cap <= 0`, which the caller renders as "Unlimited".
// The `cap <= 0 ⇒ Unlimited` sentinel is now reserved for a GENUINELY-unlimited
// (future paid) cap: un-resolved/free owners receive concrete Spark caps from
// the backend (ResolveCaps floors them server-side), so their meters fill
// instead of showing "Unlimited". Trust the wire — never floor cap client-side.
export function percent(used: number, cap: number): number | null {
  if (!Number.isFinite(cap) || cap <= 0) return null
  if (!Number.isFinite(used) || used <= 0) return 0
  const ratio = (used / cap) * 100
  if (ratio < 0) return 0
  if (ratio > 100) return 100
  return ratio
}
